'use client';
import React, { useState, useMemo } from 'react';
import { Search, Filter, Plus, Download, RefreshCw } from 'lucide-react';
import BurialTable from './BurialTable';
import BurialApprovalModal from './BurialApprovalModal';
import NewBurialModal from './NewBurialModal';

export type BurialApplication = {
  id: string;
  appId: string;
  deceased: string;
  idNumber: string;
  dateOfDeath: string;
  cemetery: string;
  block: string;
  plot: string;
  plotStatus: string;
  status: string;
  permitStatus: string;
  scheduledDate: string;
  officer: string;
  applicant: string;
  relationship: string;
  submittedDate: string;
};

export const burialApplications: BurialApplication[] = [
  { id: 'app-001', appId: 'APP-2026-0851', deceased: 'Nokwanda Zulu', idNumber: '7803045678082', dateOfDeath: '01 Aug 2026', cemetery: 'Mthatha', block: 'B4', plot: 'B4-R3-022', plotStatus: 'reserved', status: 'scheduled', permitStatus: 'Uploaded', scheduledDate: '04 Aug 2026', officer: 'T. Mthembu', applicant: 'Siphamandla Zulu', relationship: 'Son', submittedDate: '02 Aug 2026' },
  { id: 'app-002', appId: 'APP-2026-0849', deceased: 'Bongani Ntanzi', idNumber: '5501126789012', dateOfDeath: '30 Jul 2026', cemetery: 'Libode', block: 'A2', plot: 'A2-R1-007', plotStatus: 'occupied', status: 'completed', permitStatus: 'Uploaded', scheduledDate: '04 Aug 2026', officer: 'S. Madikizela', applicant: 'Zanele Ntanzi', relationship: 'Wife', submittedDate: '31 Jul 2026' },
  { id: 'app-003', appId: 'APP-2026-0847', deceased: 'Nomvula Dlamini', idNumber: '6412087654321', dateOfDeath: '02 Aug 2026', cemetery: 'Mthatha', block: 'C1', plot: 'C1-R4-031', plotStatus: 'reserved', status: 'pending', permitStatus: 'Pending', scheduledDate: '05 Aug 2026', officer: 'Unassigned', applicant: 'Lungelo Dlamini', relationship: 'Brother', submittedDate: '03 Aug 2026' },
  { id: 'app-004', appId: 'APP-2026-0845', deceased: 'Lungelo Maphumulo', idNumber: '4809234512678', dateOfDeath: '28 Jul 2026', cemetery: 'Ngqeleni', block: 'A1', plot: 'A1-R2-014', plotStatus: 'occupied', status: 'completed', permitStatus: 'Uploaded', scheduledDate: '03 Aug 2026', officer: 'N. Gqomo', applicant: 'Thandi Maphumulo', relationship: 'Daughter', submittedDate: '29 Jul 2026' },
  { id: 'app-005', appId: 'APP-2026-0843', deceased: 'Thandi Nxumalo', idNumber: '5104152345678', dateOfDeath: '27 Jul 2026', cemetery: 'Mthatha', block: 'B2', plot: 'B2-R1-009', plotStatus: 'occupied', status: 'completed', permitStatus: 'Uploaded', scheduledDate: '02 Aug 2026', officer: 'T. Mthembu', applicant: 'Musa Nxumalo', relationship: 'Husband', submittedDate: '28 Jul 2026' },
  { id: 'app-006', appId: 'APP-2026-0841', deceased: 'Sipho Khumalo', idNumber: '7209185432109', dateOfDeath: '02 Aug 2026', cemetery: 'Libode', block: 'A3', plot: 'A3-R2-018', plotStatus: 'reserved', status: 'pending', permitStatus: 'Missing', scheduledDate: '05 Aug 2026', officer: 'Unassigned', applicant: 'Nokuthula Khumalo', relationship: 'Wife', submittedDate: '03 Aug 2026' },
  { id: 'app-007', appId: 'APP-2026-0839', deceased: 'Mandla Sithole', idNumber: '6007145678901', dateOfDeath: '25 Jul 2026', cemetery: 'Mthatha', block: 'D1', plot: 'D1-R1-003', plotStatus: 'reserved', status: 'under review', permitStatus: 'Uploaded', scheduledDate: '06 Aug 2026', officer: 'T. Mthembu', applicant: 'Buhle Sithole', relationship: 'Daughter', submittedDate: '26 Jul 2026' },
  { id: 'app-008', appId: 'APP-2026-0837', deceased: 'Nosipho Cele', idNumber: '5803074321098', dateOfDeath: '24 Jul 2026', cemetery: 'Ngqeleni', block: 'B1', plot: 'B1-R3-011', plotStatus: 'occupied', status: 'completed', permitStatus: 'Uploaded', scheduledDate: '01 Aug 2026', officer: 'N. Gqomo', applicant: 'Siyanda Cele', relationship: 'Son', submittedDate: '25 Jul 2026' },
  { id: 'app-009', appId: 'APP-2026-0835', deceased: 'Vusi Mkhize', idNumber: '4506085432109', dateOfDeath: '22 Jul 2026', cemetery: 'Libode', block: 'B2', plot: 'B2-R4-027', plotStatus: 'occupied', status: 'completed', permitStatus: 'Uploaded', scheduledDate: '30 Jul 2026', officer: 'S. Madikizela', applicant: 'Lindiwe Mkhize', relationship: 'Wife', submittedDate: '23 Jul 2026' },
  { id: 'app-010', appId: 'APP-2026-0833', deceased: 'Ntombizodwa Gumede', idNumber: '6811236789012', dateOfDeath: '20 Jul 2026', cemetery: 'Mthatha', block: 'C2', plot: 'C2-R2-019', plotStatus: 'occupied', status: 'rejected', permitStatus: 'Uploaded', scheduledDate: '—', officer: 'T. Mthembu', applicant: 'Phiwayinkosi Gumede', relationship: 'Son', submittedDate: '21 Jul 2026' },
];

const STATUS_TABS = ['All', 'Pending', 'Under Review', 'Scheduled', 'Completed', 'Rejected'];

export default function BurialManagementContent() {
  const [activeTab, setActiveTab] = useState('All');
  const [search, setSearch] = useState('');
  const [selectedApp, setSelectedApp] = useState<BurialApplication | null>(null);
  const [showNewModal, setShowNewModal] = useState(false);

  const filtered = useMemo(() => {
    return burialApplications.filter((a) => {
      const matchTab = activeTab === 'All' || a.status.toLowerCase() === activeTab.toLowerCase();
      const matchSearch =
        !search ||
        a.deceased.toLowerCase().includes(search.toLowerCase()) ||
        a.appId.toLowerCase().includes(search.toLowerCase()) ||
        a.idNumber.includes(search);
      return matchTab && matchSearch;
    });
  }, [activeTab, search]);

  const tabCounts = useMemo(() => {
    const counts: Record<string, number> = { All: burialApplications.length };
    STATUS_TABS.slice(1).forEach((t) => {
      counts[t] = burialApplications.filter((a) => a.status.toLowerCase() === t.toLowerCase()).length;
    });
    return counts;
  }, []);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-700 text-foreground">Burial Management</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Process applications, approve permits, and schedule burials
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-ghost text-sm"><Download size={15} /> Export</button>
          <button className="btn-secondary text-sm"><RefreshCw size={14} /> Refresh</button>
          <button onClick={() => setShowNewModal(true)} className="btn-primary text-sm">
            <Plus size={15} /> New Application
          </button>
        </div>
      </div>

      {/* Status Tabs */}
      <div className="flex items-center gap-1 border-b border-border overflow-x-auto scrollbar-thin pb-0">
        {STATUS_TABS.map((tab) => (
          <button
            key={`burial-tab-${tab}`}
            onClick={() => setActiveTab(tab)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-500 whitespace-nowrap border-b-2 transition-all duration-150 ${
              activeTab === tab
                ? 'border-primary text-primary font-600' :'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab}
            {tabCounts[tab] > 0 && (
              <span
                className={`text-[11px] font-600 px-1.5 py-0.5 rounded-full ${
                  activeTab === tab ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                }`}
              >
                {tabCounts[tab]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Search + Filter Bar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by name, app ID, or ID number..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-base pl-9 text-sm"
          />
        </div>
        <button className="btn-ghost text-sm border border-border">
          <Filter size={14} /> Filter
        </button>
        <span className="text-sm text-muted-foreground ml-auto">
          {filtered.length} application{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      <BurialTable applications={filtered} onSelect={setSelectedApp} />

      {/* Approval Modal */}
      {selectedApp && (
        <BurialApprovalModal
          application={selectedApp}
          onClose={() => setSelectedApp(null)}
        />
      )}

      {/* New Burial Modal */}
      {showNewModal && <NewBurialModal onClose={() => setShowNewModal(false)} />}
    </div>
  );
}